// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable global-require, no-underscore-dangle, no-param-reassign, no-void, no-shadow */
/* eslint-disable func-names */

const assign = require('lodash/assign');
const eventing = require('../../helpers/eventing');

module.exports = function SubscriberPeerConnectionFactory(deps = {}) {
  const ExceptionCodes = deps.ExceptionCodes || require('../exception_codes.js');
  const OTHelpers = deps.OTHelpers || require('../../common-js-helpers/OTHelpers.js');
  const parseIceServers = deps.parseIceServers || require('../../RaptorSession/raptor/parseIceServers.js').parseIceServers;
  const PeerConnection = deps.PeerConnection || require('./peer_connection.js')();
  const RaptorConstants = deps.RaptorConstants || require('../../RaptorSession/raptor/RaptorConstants.js');
  const setCertificates = deps.setCertificates || require('./set_certificates.js')();

  /*
   * Abstracts PeerConnection related stuff away from Subscriber.
   *
   * Responsible for:
   * * setting up the underlying PeerConnection (delegates to PeerConnections)
   * * triggering a connected event when the Peer connection is opened
   * * triggering a disconnected event when the Peer connection is closed
   * * creating a video element when a stream is added
   * * responding to stream removed intelligently
   * * providing a destroy method
   * * providing a processMessage method
   *
   * Once the PeerConnection is connected and the video element playing it
   * triggers the connected event
   *
   * Triggers the following events
   * * connected
   * * disconnected
   * * remoteStreamAdded
   * * remoteStreamRemoved
   * * error
   *
   */

  return function SubscriberPeerConnection({
    clientCandidates,
    iceConfig,
    socket,
    stream,
    subscriberId,
    subscriberUri,
    properties,
    logAnalyticsEvent,
    p2p,
  }) {
    const _subscriberPeerConnection = this;
    let _peerConnection;
    let _destroyed = false;
    let _awaitingIceRestart = false;

    // Private
    const _onPeerClosed = function () {
      this.destroy();
      if (_awaitingIceRestart) {
        this.trigger('iceRestartFailure', this);
      }
      this.trigger('disconnected', this);
    };

    const _onRemoteStreamAdded = function (remoteRTCStream) {
      this.trigger('remoteStreamAdded', remoteRTCStream, this);
    };

    const _onRemoteStreamRemoved = function (remoteRTCStream) {
      this.trigger('remoteStreamRemoved', remoteRTCStream, this);
    };

    const _onRemoteVideoSupported = (supported) => {
      this.trigger('remoteVideoSupported', supported);
    };

    // Note: All Peer errors are fatal right now.
    const _onPeerError = function (errorReason, prefix) {
      this.trigger('error', null, errorReason, this, prefix);
    };

    const _onIceConnectionStateChange = function (state) {
      if (_awaitingIceRestart && (state === 'connected' || state === 'completed')) {
        _awaitingIceRestart = false;
        this.trigger('iceRestartSuccess');
      }
      this.trigger('iceConnectionStateChange', state);
    };

    const _onsignalingStateChange = function (state) {
      this.trigger('signalingStateChange', state);
    };

    const _onsignalingStateStable = function (state) {
      this.trigger('signalingStateStable', state);
    };

    const _relayMessageToPeer = function sendMessageToPeer(type, payload) {
      switch (type) {
        case RaptorConstants.Actions.ANSWER:
        case RaptorConstants.Actions.PRANSWER:
          this.trigger('connected');
          socket.jsepAnswer(subscriberUri, payload.sdp);
          break;

        case RaptorConstants.Actions.OFFER:
          socket.jsepOffer(subscriberUri, payload.sdp);
          break;

        case RaptorConstants.Actions.CANDIDATE:
          socket.jsepCandidate(subscriberUri, payload);
          break;

        default:
      }
    }.bind(this);

    eventing(this);

    // Public
    this.destroy = function () {
      if (_destroyed) { return; }
      _destroyed = true;

      if (_peerConnection) {
        // Unsubscribe us from the stream, if it hasn't already been destroyed
        if (socket.is('connected') && !stream.destroyed) {
          // Notify the server components
          socket.subscriberDestroy(stream.id, subscriberId);
        }

        // Ref: OPENTOK-2458 disable all audio tracks before removing it.
        this.subscribeToAudio(false);
        _peerConnection.disconnect();
      }

      _peerConnection = null;
      this.off();
    };

    this.getDataChannel = function (label, options, completion) {
      _peerConnection.getDataChannel(label, options, completion);
    };

    this.processMessage = function (type, message) {
      _peerConnection.processMessage(type, message);
    };

    this.remoteDescription = function () {
      return _peerConnection.remoteDescription();
    };

    this.getStats = function (callback) {
      if (_peerConnection) {
        _peerConnection.getStats(callback);
      } else {
        callback(new OTHelpers.Error('Subscriber is not connected cannot getStats',
          'NotConnectedError', {
            code: 1015,
          }));
      }
    };

    // Helper method used by subscribeToAudio/subscribeToVideo
    const _createSetEnabledForTracks = function (kind) {
      return function (enabled) {
        if (!_peerConnection) {
          // We haven't created the peer connection yet, so there are no remote streams right now.
          // Subscriber will try again after onRemoteStreamAdded so this works out ok.
          return;
        }

        _peerConnection.remoteTracks().forEach((track) => {
          if (track.kind === kind && track.enabled !== enabled) {
            track.enabled = enabled;
          }
        });
      };
    };

    this.subscribeToAudio = _createSetEnabledForTracks('audio');
    this.subscribeToVideo = _createSetEnabledForTracks('video');

    this.hasRelayCandidates = function () {
      return _peerConnection.hasRelayCandidates();
    };

    this.iceRestart = function () {
      _awaitingIceRestart = true;
      return _peerConnection.iceRestart();
    };

    this.iceConnectionStateIsConnected = function () {
      return _peerConnection.iceConnectionStateIsConnected();
    };

    // Init
    this.init = function (completion) {
      const pcConfig = { iceConfig };

      setCertificates(pcConfig, (err, pcConfigWithCerts) => {
        if (err) {
          completion(err);
          return;
        }

        const peerConnectionConfig = assign(
          {
            logAnalyticsEvent,
            clientCandidates,
          },
          pcConfigWithCerts
        );

        _peerConnection = new PeerConnection(
          assign({ sendMessage: _relayMessageToPeer, p2p }, peerConnectionConfig)
        );

        _peerConnection.on({
          iceConnected: () => _subscriberPeerConnection.emit('iceConnected'),
          close: _onPeerClosed,
          streamAdded: _onRemoteStreamAdded,
          streamRemoved: _onRemoteStreamRemoved,
          signalingStateChange: _onsignalingStateChange,
          signalingStateStable: _onsignalingStateStable,
          error: _onPeerError,
          qos: qos => this.trigger('qos', qos),
          iceConnectionStateChange: _onIceConnectionStateChange,
          remoteVideoSupported: _onRemoteVideoSupported,
        }, _subscriberPeerConnection);

        // If there are already remoteStreams, add them immediately
        // (Using .remoteTracks to avoid deprecated .remoteStreams where possible.
        // FIXME: Is this even possible anyway? How could we already have remote streams in the same
        // tick the peer connection was created?)
        if (_peerConnection.remoteTracks().length > 0) {
          _peerConnection.remoteStreams().forEach(_onRemoteStreamAdded, _subscriberPeerConnection);
        } else {
          // We only bother with the PeerConnection negotiation if we don't already
          // have a remote stream.

          let channelsToSubscribeTo;

          if (properties.subscribeToVideo || properties.subscribeToAudio) {
            const audio = stream.getChannelsOfType('audio');
            const video = stream.getChannelsOfType('video');

            channelsToSubscribeTo = audio.map(channel => ({
              id: channel.id,
              type: channel.type,
              active: properties.subscribeToAudio,
            })).concat(video.map((channel) => {
              const props = {
                id: channel.id,
                type: channel.type,
                active: properties.subscribeToVideo,
                restrictFrameRate: properties.restrictFrameRate !== void 0 ?
                  properties.restrictFrameRate : false,
              };

              if (properties.preferredFrameRate !== void 0) {
                props.preferredFrameRate = parseFloat(properties.preferredFrameRate);
              }

              if (properties.preferredHeight !== void 0) {
                props.preferredHeight = parseInt(properties.preferredHeight, 10);
              }

              if (properties.preferredWidth !== void 0) {
                props.preferredWidth = parseInt(properties.preferredWidth, 10);
              }

              return props;
            }));
          }

          socket.subscriberCreate(
            stream.id,
            subscriberId,
            channelsToSubscribeTo,
            (err, message) => {
              if (err) {
                let msg = `subscriberCreate failed with unexpected code ${err.code}`;
                let code = Number(err.code);

                if (code === 404) {
                  msg = 'stream not found';
                }

                if (code === 409 && err.message === 'streamLimitExceeded') {
                  code = ExceptionCodes.STREAM_LIMIT_EXCEEDED;
                  msg = 'The limit for concurrent streams in the session has been reached';
                }

                _subscriberPeerConnection.trigger(
                  'error',
                  code,
                  msg,
                  _subscriberPeerConnection,
                  'Subscribe'
                );
              }
              if (_peerConnection && iceConfig.needRumorIceServersFallback) {
                _peerConnection.setIceServers([
                  ...parseIceServers(message),
                  ...iceConfig.servers,
                ]);
              }
            }
          );

          completion(undefined, _subscriberPeerConnection);
        }
      });
    };
  };
};



// WEBPACK FOOTER //
// ./src/js/ot/peer_connection/subscriber_peer_connection.js